import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Content,
  Input,
  PageContainer,
  StyleButton,
  TitleContainer,
  TitleRightSide,
} from './OrganizationMembersPage.styles';
import { Text24Bold } from '../../../components/utils/Text/Text.styles';
import { CreateOrganizationMemberModal, Table } from '../../../components';
import debounce from 'lodash.debounce';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { selectFetchOrganizationMembersInfo } from '../../../store/slices/chatter/slice';
import {
  fetchAllOrganizationChatters,
  fetchAllOrganizationTeamLeads,
  fetchOrganizationMembersInfo,
} from '../../../store/slices/chatter/asyncThunks';
import OrganizationMemberItem from '../../../components/Table/items/OrganizationMemberItem/OrganizationMemberItem';
import { splitByUppercase } from '../../../utils/util';

const OrganizationMembersPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = +searchParams.get('page') || 1;
  const search = searchParams.get('search') || '';
  const perPage = +searchParams.get('perPage') || 25;
  const [searchValue, setSearchValue] = useState(() => search ?? '');
  const { data, total, pending } = useSelector(selectFetchOrganizationMembersInfo);
  const dispatch = useDispatch();
  const createMemberModal = useRef();
  const [selectedMemberId, setSelectedMemberId] = useState(null);

  useEffect(() => {
    if (page > 0) {
      dispatch(fetchOrganizationMembersInfo({ page, search: search, perPage }));
    }
  }, [dispatch, page, search, perPage]);

  const setPage = useCallback(
    page => {
      const params = new URLSearchParams(searchParams);
      params.set('page', page);
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );
  const setPerPage = useCallback(
    value => {
      const params = new URLSearchParams(searchParams);
      params.set('perPage', value);
      params.set('page', 1);
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const changeHandler = useCallback(
    text => {
      const params = new URLSearchParams(searchParams);
      params.set('search', text);
      params.set('page', 1);
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const debouncedChangeHandler = useMemo(() => debounce(changeHandler, 300), [changeHandler]);

  const tableHeader = [{ label: 'Name' }, { label: 'Email' }, { label: 'Role' }, { type: 'action' }];

  return (
    <PageContainer>
      <TitleContainer>
        <Text24Bold>Members</Text24Bold>
        <TitleRightSide>
          <Input
            hideErrorMessage
            placeholder="Search"
            value={searchValue}
            onChange={event => {
              setSearchValue(event.target.value);
              debouncedChangeHandler(event.target.value);
            }}
          />
          <StyleButton title="Create New" onClick={() => createMemberModal.current.show()} />
        </TitleRightSide>
      </TitleContainer>
      <Content>
        <Table
          headerInformation={tableHeader}
          dataLoading={pending}
          totalCount={total}
          currentPage={page}
          perPage={perPage}
          setPerPage={setPerPage}
          setPage={setPage}
          emptyDataText={'No data'}
          showPerPage
          fullHeight>
          {data.map(item => (
            <OrganizationMemberItem
              key={item._id}
              id={item._id}
              name={item.name}
              email={item.email}
              role={splitByUppercase(item.memberType)}
              onDetailsClicked={() => {
                setSelectedMemberId(item._id);
                createMemberModal.current.show();
              }}
            />
          ))}
        </Table>
      </Content>
      <CreateOrganizationMemberModal
        modalRef={createMemberModal}
        selectedMemberId={selectedMemberId}
        setSelectedMemberId={setSelectedMemberId}
        onSuccess={() => {
          dispatch(fetchOrganizationMembersInfo({ page: 1, search: '', perPage: 25 }));
        }}
        resetData={() => {
          dispatch(fetchAllOrganizationChatters());
          dispatch(fetchAllOrganizationTeamLeads());
        }}
      />
    </PageContainer>
  );
};

export default OrganizationMembersPage;
